import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Divider, Button, useTheme } from '@mui/material';
import { Room, Etage } from '../types';
import SupplierInfo from './SupplierInfo';
import TermsAndConditions from './TermsAndConditions';
import ProjectDetails from './ProjectDetails';
import CostSummary from './CostSummary';
import './SubmissionReport.css';

interface FloorData {
  selectedRooms: string[];
  roomDetails: Record<string, { customName?: string; notes?: string }>;
  items: { itemId: string; quantity: number; roomId: string; isCustom?: boolean; unitPrice?: number }[];
}

interface SubmissionReportProps {
  formData: {
    clientName: string;
    notes: string;
    date: string;
  };
  floorSelections: Record<string, FloorData>;
  rooms: Room[];
  etages: Etage[];
}

const SubmissionReport: React.FC<SubmissionReportProps> = ({ formData, floorSelections, rooms, etages }) => {
  const theme = useTheme();
  const [isClientVersion, setIsClientVersion] = useState(false);
  
  // Add debug logging
  console.log('SubmissionReport Data:', {
    formData,
    floorSelections,
    rooms,
    etages
  });

  const [quoteNumber, setQuoteNumber] = useState<number>(() => {
    // Get the last quote number from localStorage or start from 1
    const lastQuote = localStorage.getItem('lastQuoteNumber');
    return lastQuote ? parseInt(lastQuote) + 1 : 1;
  });

  useEffect(() => {
    // Save the new quote number to localStorage
    localStorage.setItem('lastQuoteNumber', quoteNumber.toString());
  }, [quoteNumber]);

  const handlePrint = (clientVersion: boolean = false) => {
    setIsClientVersion(clientVersion);
    // Wait for state to update before printing
    requestAnimationFrame(() => {
      window.print();
      // Reset state after print dialog is closed
      window.addEventListener('afterprint', () => {
        setIsClientVersion(false);
      }, { once: true });
    });
  };

  return (
    <Box 
      className="print-area" 
      sx={{ 
        '@media print': { padding: 2 },
        minHeight: '100vh',
        py: 4
      }}
    >
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'flex-end', 
        mb: 3, 
        '@media print': { display: 'none' },
        maxWidth: '1200px',
        mx: 'auto',
        px: 2,
        gap: 2
      }}>
        <Button 
          variant="contained" 
          onClick={() => handlePrint(false)}
          sx={{
            backgroundColor: theme.palette.primary.main,
            '&:hover': {
              backgroundColor: theme.palette.primary.dark,
            }
          }}
        >
          Imprimer le devis
        </Button>
        <Button 
          variant="outlined" 
          onClick={() => handlePrint(true)}
          sx={{
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            '&:hover': {
              borderColor: theme.palette.primary.dark,
              backgroundColor: 'rgba(25, 118, 210, 0.04)',
            }
          }}
        >
          Imprimer le devis - client
        </Button>
      </Box>

      <Paper 
        elevation={3}
        sx={{ 
          p: 4,
          maxWidth: '1200px',
          mx: 'auto',
          '@media print': { 
            boxShadow: 'none',
            p: 2,
            margin: 0,
            maxWidth: '100%',
          }
        }}
      >
        {/* Header Section */}
        <Grid container spacing={2} alignItems="flex-start" className="print-header-row" sx={{ mb: 2 }}>
          {/* Supplier Info */}
          <Grid item xs={12} md={6} className="print-header-col">
            <SupplierInfo />
          </Grid>

          {/* Document Details */}
          <Grid item xs={12} md={6} className="print-header-col">
            <Box sx={{ 
              textAlign: 'right',
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'flex-start',
              '@media print': {
                backgroundColor: 'white',
                p: 2,
                boxShadow: 'none',
              }
            }}>
              <Box sx={{ mb: 2 }}>
                <Typography 
                  variant="h4" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.primary.main,
                    mb: 1
                  }}
                >
                  Devis
                </Typography>
                <Typography 
                  variant="h6" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    fontWeight: 500
                  }}
                >
                  N° {quoteNumber.toString().padStart(4, '0')}
                </Typography>
              </Box>

              {/* Customer Information */}
              <Box sx={{ 
                mt: 1,
                textAlign: 'right'
              }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    mb: 1
                  }}
                >
                  <strong>Nom du client:</strong> {formData.clientName}
                </Typography>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    color: theme.palette.text.secondary,
                    mb: 2
                  }}
                >
                  <strong>Date prévue:</strong> {formData.date}
                </Typography>
                {formData.notes && (
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      color: theme.palette.text.secondary,
                      fontStyle: 'italic',
                      mt: 2,
                      maxWidth: '300px',
                      marginLeft: 'auto'
                    }}
                  >
                    <strong>Notes:</strong> {formData.notes}
                  </Typography>
                )}
              </Box>
            </Box>
          </Grid>
        </Grid>

        <Divider sx={{ my: 4 }} />

        {/* Cost Summary */}
        <Box className="cost-summary">
          <CostSummary 
            floorSelections={floorSelections}
            rooms={rooms}
            etages={etages}
          />
        </Box>

        {/* Project Details */}
        <ProjectDetails 
          floorSelections={floorSelections}
          rooms={rooms}
          etages={etages}
          hidePricing={isClientVersion}
        />

        {/* Terms and Conditions */}
        <Box sx={{ mb: 4 }} className="terms-and-conditions">
          <Typography 
            variant="h5" 
            sx={{ 
              fontWeight: 600,
              mb: 3,
              color: theme.palette.primary.main,
              borderBottom: `2px solid ${theme.palette.primary.main}`,
              pb: 1
            }}
          >
            Conditions Générales
          </Typography>
          <Box sx={{ 
            backgroundColor: 'white',
            p: 3,
            borderRadius: 1,
            boxShadow: theme.shadows[1]
          }}>
            <TermsAndConditions />
          </Box>
        </Box>
      </Paper>
    </Box>
  );
};

export default SubmissionReport; 